import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../utils/authHeaders';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const WorkScheduleList = () => {
    const companyId = localStorage.getItem('selectedCompanyId');
    const authConfig = () => ({ headers: getAuthHeaders() });
    const [schedules, setSchedules] = useState([]);
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
    const [error, setError] = useState(null);

    // Form state - matches hr_work_schedules columns exactly
    const [hrProfileId, setHrProfileId] = useState('');
    const [workingDays, setWorkingDays] = useState(['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']);
    const [weeklyHolidays, setWeeklyHolidays] = useState(['Saturday', 'Sunday']);
    const [startTime, setStartTime] = useState('09:00');
    const [endTime, setEndTime] = useState('17:00');
    const [breakDuration, setBreakDuration] = useState(60);
    const [latenessGracePeriod, setLatenessGracePeriod] = useState(15);
    const [minimumRequiredHours, setMinimumRequiredHours] = useState(8);
    const [overtimeThreshold, setOvertimeThreshold] = useState(8);
    const [overnightShiftSupport, setOvernightShiftSupport] = useState(false);

    const fetchData = async () => {
        try {
            const [schedRes, profRes] = await Promise.all([
                axios.get(`${API_BASE}/companies/${companyId}/work-schedules`, authConfig()),
                axios.get(`${API_BASE}/companies/${companyId}/hr-profiles`, authConfig())
            ]);
            setSchedules(schedRes.data.data);
            setProfiles(profRes.data.data);

            if (profRes.data.data.length > 0) {
                setHrProfileId(profRes.data.data[0].id);
            }
        } catch (err) {
            setError('Failed to fetch work schedules.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [companyId]);

    const toggleDay = (day, list, setList) => {
        if (list.includes(day)) {
            setList(list.filter(d => d !== day));
        } else {
            setList([...list, day]);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API_BASE}/companies/${companyId}/work-schedules`, {
                hr_profile_id: hrProfileId,
                working_days: workingDays,
                weekly_holidays: weeklyHolidays,
                start_time: startTime,
                end_time: endTime,
                break_duration: breakDuration,
                lateness_grace_period: latenessGracePeriod,
                minimum_required_hours: minimumRequiredHours,
                overtime_threshold: overtimeThreshold,
                overnight_shift_support: overnightShiftSupport,
            }, authConfig());
            fetchData();
            alert('Work schedule created successfully!');
        } catch (err) {
            alert('Error creating work schedule.');
        }
    };

    if (initialLoading) return <div className="p-6 text-center text-gray-600">Loading work schedules...</div>;
    if (error) return <div className="p-6 text-center text-red-600">{error}</div>;

    return (
        <div className="max-w-7xl mx-auto p-6 mt-10">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Work Schedules & Shifts</h2>

            {/* Creation Form */}
            <form onSubmit={handleCreate} className="bg-white p-6 rounded-lg shadow-md mb-8 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">HR Profile</label>
                        <select
                            value={hrProfileId}
                            onChange={(e) => setHrProfileId(e.target.value)}
                            className="w-full border border-gray-300 rounded-md p-2 text-sm bg-white"
                            required
                        >
                            {profiles.map((prof) => (
                                <option key={prof.id} value={prof.id}>{prof.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Start Time</label>
                        <input
                            type="time"
                            value={startTime}
                            onChange={(e) => setStartTime(e.target.value)}
                            required
                            className="w-full border border-gray-300 rounded-md p-2 text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">End Time</label>
                        <input
                            type="time"
                            value={endTime}
                            onChange={(e) => setEndTime(e.target.value)}
                            required
                            className="w-full border border-gray-300 rounded-md p-2 text-sm"
                        />
                    </div>
                    <div className="flex items-center gap-2 pb-2">
                        <input
                            type="checkbox"
                            id="overnight"
                            checked={overnightShiftSupport}
                            onChange={(e) => setOvernightShiftSupport(e.target.checked)}
                        />
                        <label htmlFor="overnight" className="text-xs font-medium text-gray-700">Overnight shift</label>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Break (mins)</label>
                        <input
                            type="number"
                            value={breakDuration}
                            onChange={(e) => setBreakDuration(e.target.value)}
                            required
                            className="w-full border border-gray-300 rounded-md p-2 text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Lateness Grace Period (mins)</label>
                        <input
                            type="number"
                            value={latenessGracePeriod}
                            onChange={(e) => setLatenessGracePeriod(e.target.value)}
                            required
                            className="w-full border border-gray-300 rounded-md p-2 text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Minimum Required Hours</label>
                        <input
                            type="number"
                            step="0.5"
                            value={minimumRequiredHours}
                            onChange={(e) => setMinimumRequiredHours(e.target.value)}
                            required
                            className="w-full border border-gray-300 rounded-md p-2 text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Overtime Threshold (hrs)</label>
                        <input
                            type="number"
                            step="0.5"
                            value={overtimeThreshold}
                            onChange={(e) => setOvertimeThreshold(e.target.value)}
                            required
                            className="w-full border border-gray-300 rounded-md p-2 text-sm"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Working Days</label>
                        <div className="flex flex-wrap gap-2">
                            {DAYS.map(day => (
                                <label key={day} className={`px-2 py-1 rounded-md text-xs border cursor-pointer ${workingDays.includes(day) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`}>
                                    <input
                                        type="checkbox"
                                        className="hidden"
                                        checked={workingDays.includes(day)}
                                        onChange={() => toggleDay(day, workingDays, setWorkingDays)}
                                    />
                                    {day.slice(0, 3)}
                                </label>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Weekly Holidays</label>
                        <div className="flex flex-wrap gap-2">
                            {DAYS.map(day => (
                                <label key={day} className={`px-2 py-1 rounded-md text-xs border cursor-pointer ${weeklyHolidays.includes(day) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`}>
                                    <input
                                        type="checkbox"
                                        className="hidden"
                                        checked={weeklyHolidays.includes(day)}
                                        onChange={() => toggleDay(day, weeklyHolidays, setWeeklyHolidays)}
                                    />
                                    {day.slice(0, 3)}
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                <button type="submit" className="bg-blue-600 text-white py-2 px-6 rounded-md hover:bg-blue-700 text-sm font-medium">
                    + Add Schedule
                </button>
            </form>

            {/* Schedules Table */}
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">HR Profile</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Working Days</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Weekly Holidays</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timing</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grace Period</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {schedules.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="px-6 py-4 text-center text-gray-500">No work schedules found. Create your first one above!</td>
                            </tr>
                        ) : (
                            schedules.map((sch) => (
                                <tr key={sch.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{sch.hr_profile ? sch.hr_profile.name : 'N/A'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{(sch.working_days || []).map(d => d.slice(0, 3)).join(', ')}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{(sch.weekly_holidays || []).map(d => d.slice(0, 3)).join(', ')}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{sch.start_time} - {sch.end_time}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{sch.lateness_grace_period} mins</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default WorkScheduleList;
